import { makeStyles } from '@material-ui/core/styles';

import { Text } from '@backstage/ui';

import type { PreviewField } from '../../extensions';
import {
  ArrayValue,
  ExtraFields,
  FieldValue,
  JsonFallback,
  ScalarValue,
} from './ValueRenderers';
import {
  getDefaultPreviewFields,
  getNestedValue,
  hasNestedKey,
  humanizeKey,
  isPlainObject,
  isRenderableScalar,
  isScalarArray,
  resolveInputBody,
} from './inputUtils';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--bui-space-2)',
    marginTop: 'var(--bui-space-2)',
  },
  row: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--bui-space-1)',
  },
});

export const ToolInputPreview = ({
  input,
  previewFields,
}: {
  input?: unknown;
  previewFields?: PreviewField[];
}) => {
  const classes = useStyles();

  if (input === null || input === undefined) {
    return null;
  }

  if (!isPlainObject(input)) {
    return <JsonFallback input={input} />;
  }

  const inputObj = resolveInputBody(input);
  const entries = Object.entries(inputObj);
  if (entries.length === 0) {
    return null;
  }

  const effectiveFields =
    previewFields && previewFields.length > 0
      ? previewFields
      : getDefaultPreviewFields(inputObj);
  const hasMatchingFields = effectiveFields.some(f =>
    hasNestedKey(inputObj, f.key),
  );

  if (hasMatchingFields) {
    const topLevelPreviewKeys = new Set(
      effectiveFields.map(f => f.key.split('.')[0]),
    );
    const importantEntries = effectiveFields
      .filter(f => hasNestedKey(inputObj, f.key))
      .map(f => ({
        key: f.key,
        label: f.label ?? humanizeKey(f.key.split('.').pop()!),
        value: getNestedValue(inputObj, f.key),
      }));
    const extraEntries = entries.filter(
      ([key]) => !topLevelPreviewKeys.has(key),
    );

    return (
      <div className={classes.root}>
        {importantEntries.map(({ key, label, value }) => (
          <div key={key} className={classes.row}>
            <Text variant="body-small" color="secondary">
              {label}
            </Text>
            <FieldValue value={value} />
          </div>
        ))}
        {extraEntries.length > 0 && <ExtraFields entries={extraEntries} />}
      </div>
    );
  }

  const allRenderable = entries.every(
    ([, value]) => isRenderableScalar(value) || isScalarArray(value),
  );
  if (!allRenderable) {
    return <JsonFallback input={input} />;
  }

  return (
    <div className={classes.root}>
      {entries.map(([key, value]) => (
        <div key={key} className={classes.row}>
          <Text variant="body-small" color="secondary">
            {humanizeKey(key)}
          </Text>
          {isScalarArray(value) ? (
            <ArrayValue value={value} />
          ) : (
            <ScalarValue
              value={value as string | number | boolean | null | undefined}
            />
          )}
        </div>
      ))}
    </div>
  );
};
